// Frozen cross-service contract (ENG-2488) — one string serves as cookie name, header name, and
// localStorage key; changing it here silently breaks the backend's header-then-cookie resolution.
export const DEVICE_ID_KEY = 'X-UTR-Device-Id';

export const DEVICE_ID_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2;

const ROOT_DOMAIN = 'utrsports.net';

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const getCrypto = () => {
    if (typeof window !== 'undefined' && window.crypto) {
        return window.crypto;
    }
    return null;
};

const UUID_BYTE_LENGTH = 16;
const UUID_VERSION_BYTE_INDEX = 6;
const UUID_VARIANT_BYTE_INDEX = 8;
const UUID_GROUP_BREAK_INDEXES = [4, 6, 8, 10];

const buildUuidV4FromRandomValues = (cryptoObj) => {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(UUID_BYTE_LENGTH));

    /* eslint-disable no-bitwise */
    bytes[UUID_VERSION_BYTE_INDEX] = (bytes[UUID_VERSION_BYTE_INDEX] & 0x0f) | 0x40;
    bytes[UUID_VARIANT_BYTE_INDEX] = (bytes[UUID_VARIANT_BYTE_INDEX] & 0x3f) | 0x80;
    /* eslint-enable no-bitwise */

    return Array.from(bytes)
        .map((byte, index) => {
            const hex = byte.toString(16).padStart(2, '0');
            return UUID_GROUP_BREAK_INDEXES.includes(index) ? `-${hex}` : hex;
        })
        .join('');
};

export const generateDeviceId = () => {
    const cryptoObj = getCrypto();
    if (!cryptoObj) {
        return null;
    }
    if (typeof cryptoObj.randomUUID === 'function') {
        return cryptoObj.randomUUID();
    }
    if (typeof cryptoObj.getRandomValues === 'function') {
        return buildUuidV4FromRandomValues(cryptoObj);
    }
    return null;
};

// A leading-dot parent domain so the cookie reaches api.utrsports.net (where tokens mint); a
// host-scoped domain would not — that is the bug the existing `_fingerprint` cookie has. On
// localhost or an IP there is no shared parent domain, so the cookie stays host-only.
export const resolveCookieDomain = (hostname) => {
    if (!hostname) {
        return null;
    }
    if (hostname === ROOT_DOMAIN || hostname.endsWith(`.${ROOT_DOMAIN}`)) {
        return `.${ROOT_DOMAIN}`;
    }
    return null;
};

// `Secure` is coupled to the parent domain: production hosts are always https, but local dev over
// http (or a LAN IP, which is not a secure context) would silently drop a Secure cookie.
export const buildDeviceIdCookie = (id, domain) => {
    const segments = [`${DEVICE_ID_KEY}=${id}`];
    if (domain) {
        segments.push(`Domain=${domain}`);
    }
    segments.push('Path=/');
    segments.push(`Max-Age=${DEVICE_ID_MAX_AGE_SECONDS}`);
    segments.push('SameSite=Lax');
    if (domain) {
        segments.push('Secure');
    }
    return segments.join('; ');
};

const readDeviceIdFromCookie = () => {
    if (typeof document === 'undefined' || !document.cookie) {
        return null;
    }
    const match = document.cookie.split('; ').find(row => row.startsWith(`${DEVICE_ID_KEY}=`));
    if (!match) {
        return null;
    }
    return match.slice(DEVICE_ID_KEY.length + 1) || null;
};

const readDeviceIdFromStorage = () => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return null;
        }
        return window.localStorage.getItem(DEVICE_ID_KEY) || null;
    } catch {
        return null;
    }
};

const writeDeviceIdToCookie = (id) => {
    if (typeof document === 'undefined') {
        return;
    }
    const hostname = (typeof window !== 'undefined' && window.location) ? window.location.hostname : '';
    document.cookie = buildDeviceIdCookie(id, resolveCookieDomain(hostname));
};

const writeDeviceIdToStorage = (id) => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }
        window.localStorage.setItem(DEVICE_ID_KEY, id);
    } catch {
        // localStorage can throw when disabled or in private mode; the cookie stays the source of truth.
    }
};

export const ensureDeviceId = () => {
    if (!isBrowser()) {
        return null;
    }
    const id = readDeviceIdFromCookie() || readDeviceIdFromStorage() || generateDeviceId();
    if (!id) {
        return null;
    }
    writeDeviceIdToCookie(id);
    writeDeviceIdToStorage(id);
    return id;
};
